import * as maplibregl from 'maplibre-gl';
import { API_URL_ADMIN } from '~/config/config';
import { showFreePhotoPopup } from '../controllers/popups/FreePhotoPopup';

export const FREE_PHOTO_SOURCE_ID = 'free-photo-tiles-source';
export const FREE_PHOTO_LAYER_ID_POINT = 'free-photo-layer-point';
export const FREE_PHOTO_LAYER_ID_LABEL = 'free-photo-layer-label';
export const FREE_PHOTO_SOURCE_LAYER = 'free_photo';
export const FREE_PHOTO_CAMERA_ICON_ID = 'free-photo-camera-icon';

let freePhotoEvents: Record<string, any> = {};
let currentProjectId: number | undefined = undefined;
let cachedCameraImg: HTMLImageElement | null = null;

const loadCameraIcon = (map: maplibregl.Map): Promise<void> => {
  if (cachedCameraImg && !map.hasImage(FREE_PHOTO_CAMERA_ICON_ID)) {
    map.addImage(FREE_PHOTO_CAMERA_ICON_ID, cachedCameraImg, { pixelRatio: 2 });
    return Promise.resolve();
  }
  if (map.hasImage(FREE_PHOTO_CAMERA_ICON_ID)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const svg = `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#2563eb" stroke="#ffffff" stroke-width="2.5" />
        <g transform="translate(10, 10)">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.5 3C4.67157 3 4 3.67157 4 4.5V5H2.5C1.67157 5 1 5.67157 1 6.5V16C1 16.8284 1.67157 17.5 2.5 17.5H17.5C18.3284 17.5 19 16.8284 19 16V6.5C19 5.67157 18.3284 5 17.5 5H16V4.5C16 3.67157 15.3284 3 14.5 3H5.5ZM10 7.5C7.79086 7.5 6 9.29086 6 11.5C6 13.7091 7.79086 15.5 10 15.5C12.2091 15.5 14 13.7091 14 11.5C14 9.29086 12.2091 7.5 10 7.5ZM10 9C8.61929 9 7.5 10.1193 7.5 11.5C7.5 12.8807 8.61929 14 10 14C11.3807 14 12.5 12.8807 12.5 11.5C12.5 10.1193 11.3807 9 10 9Z" fill="#ffffff"/>
          <circle cx="16" cy="7" r="0.8" fill="#ffffff"/>
        </g>
      </svg>
    `.trim();

    const img = new window.Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    img.onload = () => {
      cachedCameraImg = img;
      if (!map.hasImage(FREE_PHOTO_CAMERA_ICON_ID)) {
        map.addImage(FREE_PHOTO_CAMERA_ICON_ID, img, { pixelRatio: 2 });
      }
      resolve();
    };
    img.onerror = () => {
      console.error('Failed to load free photo camera icon');
      resolve();
    };
  });
};

const FreePhotoVectorTileUtils = {
  addToMap: async (projectId?: number) => {
    const map = window.map;
    if (!map) return;

    currentProjectId = projectId;

    // Remove existing layers and source
    FreePhotoVectorTileUtils._removeLayers();

    if (map.getSource(FREE_PHOTO_SOURCE_ID)) {
      map.removeSource(FREE_PHOTO_SOURCE_ID);
    }

    // Ensure camera icon is registered in map style
    await loadCameraIcon(map);

    // Build tile URL
    const tileUrl = projectId
      ? `${API_URL_ADMIN}/api/geocollect/tiles/free-photo/{z}/{x}/{y}.pbf?project_id=${projectId}`
      : `${API_URL_ADMIN}/api/geocollect/tiles/free-photo/{z}/{x}/{y}.pbf`;

    map.addSource(FREE_PHOTO_SOURCE_ID, {
      type: 'vector',
      tiles: [tileUrl],
      minzoom: 0,
      maxzoom: 22,
    });

    FreePhotoVectorTileUtils._addLayers();
    FreePhotoVectorTileUtils._addEventListeners();
  },

  _addLayers: () => {
    const map = window.map;
    if (!map) return;

    // Camera icon symbol layer (shown at all zoom levels >= 8)
    map.addLayer({
      id: FREE_PHOTO_LAYER_ID_POINT,
      type: 'symbol',
      source: FREE_PHOTO_SOURCE_ID,
      'source-layer': FREE_PHOTO_SOURCE_LAYER,
      minzoom: 8,
      layout: {
        'icon-image': FREE_PHOTO_CAMERA_ICON_ID,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, 0.65,
          12, 0.85,
          16, 1.05,
          20, 1.25,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });

    // Label layer (shown only at zoom >= 14)
    map.addLayer({
      id: FREE_PHOTO_LAYER_ID_LABEL,
      type: 'symbol',
      source: FREE_PHOTO_SOURCE_ID,
      'source-layer': FREE_PHOTO_SOURCE_LAYER,
      minzoom: 14,
      layout: {
        'text-field': ['get', 'file_name'],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-size': 10,
        'text-offset': [0, 1.6],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-max-width': 8,
      },
      paint: {
        'text-color': '#1e40af',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1,
      },
    });
  },

  _removeLayers: () => {
    const map = window.map;
    if (!map) return;

    const layerIds = [FREE_PHOTO_LAYER_ID_POINT, FREE_PHOTO_LAYER_ID_LABEL];
    layerIds.forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
  },

  moveLayerToTop: () => {
    const map = window.map;
    if (!map) return;

    [FREE_PHOTO_LAYER_ID_LABEL, FREE_PHOTO_LAYER_ID_POINT].forEach((id) => {
      if (map.getLayer(id)) {
        map.moveLayer(id);
      }
    });
  },

  _addEventListeners: () => {
    const map = window.map;
    if (!map) return;

    const onMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    const onClick = (e: any) => {
      if (!e.features || e.features.length === 0) return;
      const feature = e.features[0];
      const props = feature.properties || {};
      const lngLat = e.lngLat;

      showFreePhotoPopup({ lngLat, properties: props });
    };

    map.on('mouseenter', FREE_PHOTO_LAYER_ID_POINT, onMouseEnter);
    map.on('mouseleave', FREE_PHOTO_LAYER_ID_POINT, onMouseLeave);
    map.on('click', FREE_PHOTO_LAYER_ID_POINT, onClick);

    freePhotoEvents = { onMouseEnter, onMouseLeave, onClick };
  },

  removeFromMap: () => {
    const map = window.map;
    if (!map) return;

    // Remove event listeners
    if (freePhotoEvents.onMouseEnter) {
      map.off('mouseenter', FREE_PHOTO_LAYER_ID_POINT, freePhotoEvents.onMouseEnter);
    }
    if (freePhotoEvents.onMouseLeave) {
      map.off('mouseleave', FREE_PHOTO_LAYER_ID_POINT, freePhotoEvents.onMouseLeave);
    }
    if (freePhotoEvents.onClick) {
      map.off('click', FREE_PHOTO_LAYER_ID_POINT, freePhotoEvents.onClick);
    }
    freePhotoEvents = {};

    // Remove popup
    showFreePhotoPopup(null);

    FreePhotoVectorTileUtils._removeLayers();

    if (map.getSource(FREE_PHOTO_SOURCE_ID)) {
      map.removeSource(FREE_PHOTO_SOURCE_ID);
    }

    currentProjectId = undefined;
  },

  isLayerVisible: (): boolean => {
    const map = window.map;
    if (!map) return false;
    if (!map.getLayer(FREE_PHOTO_LAYER_ID_POINT)) return false;
    const visibility = map.getLayoutProperty(FREE_PHOTO_LAYER_ID_POINT, 'visibility');
    return visibility !== 'none';
  },

  setVisible: (visible: boolean) => {
    const map = window.map;
    if (!map) return;
    const vis = visible ? 'visible' : 'none';
    const layerIds = [FREE_PHOTO_LAYER_ID_POINT, FREE_PHOTO_LAYER_ID_LABEL];
    layerIds.forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', vis);
      }
    });
  },

  toggleLayer: () => {
    const isVisible = FreePhotoVectorTileUtils.isLayerVisible();
    FreePhotoVectorTileUtils.setVisible(!isVisible);
  },

  refreshData: () => {
    const map = window.map;
    if (!map || !map.getSource(FREE_PHOTO_SOURCE_ID)) return;

    try {
      const timestamp = Date.now();
      const tileUrl = currentProjectId
        ? `${API_URL_ADMIN}/api/geocollect/tiles/free-photo/{z}/{x}/{y}.pbf?project_id=${currentProjectId}&_t=${timestamp}`
        : `${API_URL_ADMIN}/api/geocollect/tiles/free-photo/{z}/{x}/{y}.pbf?_t=${timestamp}`;

      const source = map.getSource(FREE_PHOTO_SOURCE_ID) as any;
      if (source && source.setTiles) {
        source.setTiles([tileUrl]);
        return;
      }
      if (source && source._options) {
        source._options.tiles = [tileUrl];
        if (source.reload) {
          source.reload();
          return;
        }
      }
    } catch (error) {
      console.error('Error refreshing FreePhoto tiles:', error);
    }
  },
};

export default FreePhotoVectorTileUtils;
